import { useCallback, useEffect, useState } from "react";
import api from "../api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { eur } from "../helpers";
import { ChevronRight, ArrowLeft, Loader2, Sigma, FileText, ExternalLink } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtVal = (v, unit) => (unit === "pct" ? `${v}%` : eur(v));
const DOC_TYPES = ["booking", "maintenance", "cleaning", "payment", "expense"];

const TYPE_META = {
  booking: { tone: "text-slate-800" },
  maintenance: { tone: "text-amber-700" },
  cleaning: { tone: "text-cyan-700" },
  payment: { tone: "text-emerald-700" },
  expense: { tone: "text-rose-700" },
  cost: { tone: "text-rose-700" },
  metric: { tone: "text-indigo-700" },
};

export default function WhySheet({ apartmentId, metric, year, month, open, onOpenChange, onNavigate }) {
  const [stack, setStack] = useState([]);           // frames: {kind:'metric',metric} | {kind:'doc',rtype,rid,label}
  const [payload, setPayload] = useState(null);     // current frame data
  const [loading, setLoading] = useState(false);
  const frame = stack[stack.length - 1];

  const fetchFrame = useCallback(async (fr) => {
    if (!fr) return;
    setLoading(true);
    try {
      if (fr.kind === "metric") {
        const r = await api.get(`/apartments/${apartmentId}/kpi-breakdown`, { params: { metric: fr.metric, year, month } });
        setPayload({ kind: "metric", ...r.data });
      } else {
        const r = await api.get(`/records/${fr.rtype}/${fr.rid}`);
        setPayload({ kind: "doc", ...r.data });
      }
    } catch { setPayload(null); } finally { setLoading(false); }
  }, [apartmentId, year, month]);

  useEffect(() => { if (open && metric) setStack([{ kind: "metric", metric }]); }, [open, metric]);
  useEffect(() => { if (open && frame) fetchFrame(frame); }, [open, frame, fetchFrame]);

  const push = (fr) => setStack((s) => [...s, fr]);
  const goBack = () => setStack((s) => s.slice(0, -1));

  const clickSource = (src) => {
    if (src.type === "metric" && src.metric) { push({ kind: "metric", metric: src.metric }); return; }
    if (DOC_TYPES.includes(src.type) && src.id) { push({ kind: "doc", rtype: src.type, rid: src.id, label: src.label }); return; }
    const tab = { booking: "bookings", maintenance: "maintenance", cost: "costs", expense: "costs" }[src.type];
    if (tab && onNavigate) { onNavigate(tab, src); onOpenChange(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-hidden bg-[#F9F8F4] p-0 sm:max-w-md" data-testid="why-sheet">
        <SheetHeader className="sticky top-0 z-10 border-b border-[#EAE7DF] bg-[#F9F8F4]/90 px-5 py-4 backdrop-blur-xl">
          <div className="flex items-center gap-2">
            {stack.length > 1 && (
              <button data-testid="why-back" onClick={goBack} className="rounded-md p-1 text-slate-500 hover:bg-slate-200"><ArrowLeft size={16} /></button>
            )}
            <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-700">
              {payload?.kind === "doc" ? "Documento originale" : "Perché?"}
            </span>
          </div>
          <SheetTitle className="font-head text-lg text-slate-900" data-testid="why-title">
            {payload?.kind === "doc" ? payload.title : (payload?.label || "…")}
          </SheetTitle>
          <SheetDescription className="flex items-center justify-between">
            <span className="text-slate-500">{payload?.kind === "metric" ? payload?.period?.label : ""}</span>
            {payload?.kind === "metric" && (
              <span className="font-head text-2xl font-light tabular-nums text-slate-900" data-testid="why-value">
                {fmtVal(payload.value, payload.unit)}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && <div className="flex justify-center py-12 text-slate-400"><Loader2 className="animate-spin" /></div>}

          {!loading && payload?.kind === "metric" && (
            <>
              <div className="mb-5 rounded-lg border border-[#EAE7DF] bg-white p-4" data-testid="why-formula">
                <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><Sigma size={12} /> Come è calcolato</div>
                <div className="flex flex-wrap items-center gap-1.5 font-mono text-xs">
                  {payload.formula.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5">
                      {f.op && ["−", "+", "×", "÷", "="].includes(f.op) && <span className="text-slate-400">{f.op}</span>}
                      <span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-slate-700">
                        {f.label}{f.value != null ? ` (${fmtVal(f.value, payload.unit)})` : ""}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                {payload.sources.some((s) => s.type === "metric") ? "Componenti" : "Movimenti"} ({payload.sources.length})
              </div>
              <div className="space-y-1.5">
                {payload.sources.length === 0 && <div className="py-6 text-center text-sm text-slate-400">Nessuna riga nel periodo.</div>}
                {payload.sources.map((src, i) => {
                  const meta = TYPE_META[src.type] || {};
                  const clickable = src.type === "metric" || (DOC_TYPES.includes(src.type) && src.id) || ["booking", "maintenance", "cost", "expense"].includes(src.type);
                  const isDoc = DOC_TYPES.includes(src.type) && src.id;
                  return (
                    <button key={src.id || i} data-testid={`why-source-${i}`} disabled={!clickable}
                      onClick={() => clickSource(src)}
                      className={`flex w-full items-center justify-between rounded-lg border border-[#EAE7DF] bg-white px-3 py-2.5 text-left text-sm transition-all ${clickable ? "cursor-pointer hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-sm" : "cursor-default"}`}>
                      <div className="flex min-w-0 items-center gap-2">
                        {isDoc && <FileText size={14} className="shrink-0 text-slate-300" />}
                        <div className="min-w-0">
                          <div className={`truncate font-medium ${meta.tone || "text-slate-800"}`}>{src.label}</div>
                          {src.sublabel && <div className="truncate text-xs text-slate-400">{src.sublabel}</div>}
                        </div>
                      </div>
                      <div className="ml-2 flex items-center gap-1.5 whitespace-nowrap">
                        <span className={`tabular-nums font-medium ${src.amount < 0 ? "text-rose-600" : "text-slate-900"}`}>
                          {payload.unit === "pct" && src.type !== "metric" ? `${src.amount} notti` : eur(src.amount)}
                        </span>
                        {clickable && <ChevronRight size={15} className="text-slate-300" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {!loading && payload?.kind === "doc" && (
            <div data-testid="why-document">
              <div className="mb-4 grid grid-cols-1 gap-x-6 gap-y-1.5 rounded-lg border border-[#EAE7DF] bg-white p-4 text-sm sm:grid-cols-2">
                {payload.fields.map((f, i) => (
                  <div key={i} className="flex justify-between gap-3 border-b border-slate-50 py-1.5">
                    <span className="text-slate-500">{f.label}</span>
                    <span className={`truncate text-right font-medium text-slate-800 ${f.mono ? "font-mono text-xs" : ""}`}>{String(f.value)}</span>
                  </div>
                ))}
              </div>
              {payload.links?.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {payload.links.map((l, i) => (
                    <a key={i} href={l.url.startsWith("http") ? l.url : `${BACKEND}${l.url}`} target="_blank" rel="noreferrer"
                      data-testid={`why-doc-link-${i}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:border-amber-300 hover:text-amber-700">
                      <ExternalLink size={13} /> {l.label}
                    </a>
                  ))}
                </div>
              )}
              {payload.related?.items?.length > 0 && (
                <>
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{payload.related.label} ({payload.related.items.length})</div>
                  <div className="space-y-1.5">
                    {payload.related.items.map((it, i) => (
                      <button key={it.id || i} data-testid={`why-related-${i}`} onClick={() => clickSource(it)}
                        className="flex w-full items-center justify-between rounded-lg border border-[#EAE7DF] bg-white px-3 py-2.5 text-left text-sm transition-all hover:-translate-y-0.5 hover:border-amber-300 hover:shadow-sm">
                        <div className="min-w-0"><div className="truncate font-medium text-slate-800">{it.label}</div>
                          {it.sublabel && <div className="truncate text-xs text-slate-400">{it.sublabel}</div>}</div>
                        <ChevronRight size={15} className="text-slate-300" />
                      </button>
                    ))}
                  </div>
                </>
              )}
              {payload.nav_tab && (
                <button data-testid="why-open-section" onClick={() => { onNavigate?.(payload.nav_tab); onOpenChange(false); }}
                  className="mt-4 inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 hover:underline">
                  Apri nella sezione <ChevronRight size={13} />
                </button>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
